import React, {Fragment, useState, useEffect} from "react";
import {useDispatch} from "react-redux";
import {
    RESET_PASSWORD_SUCCESS,
    RESET_PASSWORD_CODE_SUCCESS,
    RESET_PASSWORD_NEW_PASSWORD_SUCCESS,
    resetPassword,
    resetPasswordCode,
    resetPasswordNewPassword,
} from "../../core/actions/UserActions";
import useInvisibleRecaptcha from "../../core/hooks/useInvisibleRecaptcha";
import {
    CAPTCHA_CONFIRM_MESSAGE,
    CAPTCHA_ERRORS,
    CAPTCHA_FAILED_MESSAGE,
    CAPTCHA_MAX_ATTEMPTS,
} from "../../core/constants/CaptchaConstants";
import {INVALID_EMAIL} from "../../../../common/constants/ApiErrorCode";
import ForgotPasswordForm from "../../form/components/forms/ForgotPasswordForm";
import ResetPasswordCodeForm from "../../form/components/forms/ResetPasswordCodeForm";
import ResetPasswordNewPasswordForm from "../../form/components/forms/ResetPasswordNewPasswordForm";
import {setResetPasswordStep} from "../../form/actions/PageActions";

const STEP_EMAIL = 'email';
const STEP_CODE = 'code';
const STEP_NEW_PASSWORD = 'newPassword';
const STEP_SUCCESS = 'success';

function ResetPasswordPage({onSignIn, initialEmail, initialStep}) {
    const dispatch = useDispatch();
    const [step, setStep] = useState(initialStep || STEP_EMAIL);
    const [hasOwnPassword, setHasOwnPassword] = useState(true);

    useEffect(() => {
        dispatch(setResetPasswordStep(step));
    }, [step]);

    useEffect(() => {
        return () => dispatch(setResetPasswordStep(null));
    }, []);
    const [userEmail, setUserEmail] = useState(initialEmail || '');
    const [verifiedCode, setVerifiedCode] = useState('');
    const {execute: executeRecaptcha, enabled: recaptchaEnabled} = useInvisibleRecaptcha();

    const getErrorCode = action => {
        const payload = action && action.payload ? action.payload : {};
        const responseBody = payload && payload.response ? payload.response : {};

        if (typeof responseBody.error === 'string') {
            return responseBody.error;
        }

        if (typeof responseBody.message === 'string') {
            return responseBody.message;
        }

        if (typeof responseBody.description === 'string') {
            return responseBody.description;
        }

        if (
            responseBody &&
            responseBody.errors &&
            typeof responseBody.errors.detail === 'string'
        ) {
            return responseBody.errors.detail;
        }

        const details = responseBody && responseBody.details ? responseBody.details : payload.details;
        const emailErrors = details && Array.isArray(details.email) ? details.email : [];

        if (emailErrors.length > 0) {
            return INVALID_EMAIL;
        }

        return null;
    };

    const sendResetRequest = async (values, recaptchaResponse = '', captchaAttempt = 0) => {
        let nextRecaptchaResponse = recaptchaResponse;
        let nextCaptchaAttempt = captchaAttempt;

        if (!nextRecaptchaResponse && recaptchaEnabled && nextCaptchaAttempt === 0) {
            const recaptchaToken = await executeRecaptcha();
            if (recaptchaToken) {
                nextRecaptchaResponse = recaptchaToken;
                nextCaptchaAttempt = 1;
            } else {
                return undefined;
            }
        }

        const payload = {
            ...values,
            recaptchaResponse: nextRecaptchaResponse,
        };

        const response = await dispatch(resetPassword(payload));
        const errorCode = getErrorCode(response);

        if (errorCode) {
            if (errorCode === INVALID_EMAIL) {
                return {email: 'This user does not exist. Try again.'};
            }

            if (CAPTCHA_ERRORS.includes(errorCode)) {
                if (!recaptchaEnabled || nextCaptchaAttempt >= CAPTCHA_MAX_ATTEMPTS) {
                    return {email: CAPTCHA_CONFIRM_MESSAGE};
                }

                const recaptchaToken = await executeRecaptcha();
                if (!recaptchaToken) {
                    return {email: CAPTCHA_FAILED_MESSAGE};
                }

                return sendResetRequest(values, recaptchaToken, nextCaptchaAttempt + 1);
            }

            return {email: 'Try again later.'};
        }

        if (response.type !== RESET_PASSWORD_SUCCESS) {
            return {email: 'Try again later.'};
        }

        if (!response.payload || response.payload.success !== true) {
            return {email: 'Try again later.'};
        }

        if (response.payload.response && response.payload.response.has_own_password !== undefined) {
            setHasOwnPassword(response.payload.response.has_own_password);
        }

        setStep(STEP_CODE);
        return undefined;
    };

    const onSubmitEmail = values => {
        setUserEmail(values.email);
        return sendResetRequest(values);
    };

    const onSubmitCode = async (values) => {
        const response = await dispatch(resetPasswordCode({
            email: userEmail,
            code: values.code,
        }));

        const errorCode = getErrorCode(response);

        if (errorCode) {
            return {code: 'Invalid code. Please try again.'};
        }

        if (response.type !== RESET_PASSWORD_CODE_SUCCESS) {
            return {code: 'Try again later.'};
        }

        if (!response.payload || response.payload.success !== true) {
            return {code: 'Try again later.'};
        }

        setVerifiedCode(values.code);
        setStep(STEP_NEW_PASSWORD);
        return undefined;
    };

    const onSubmitNewPassword = async (values) => {
        const response = await dispatch(resetPasswordNewPassword({
            email: userEmail,
            code: verifiedCode,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
        }));

        const errorCode = getErrorCode(response);

        if (errorCode) {
            return {newPassword: 'Failed to set password. Try again later.'};
        }

        if (response.type !== RESET_PASSWORD_NEW_PASSWORD_SUCCESS) {
            return {newPassword: 'Try again later.'};
        }

        if (!response.payload || response.payload.success !== true) {
            return {newPassword: 'Try again later.'};
        }

        setStep(STEP_SUCCESS);
        return undefined;
    };

    const handleBackToEmail = () => {
        setStep(STEP_EMAIL);
    };

    const renderStep = () => {
        switch (step) {
            case STEP_EMAIL:
                return (
                    <Fragment>
                        <div className="page-title_v3">Reset Password</div>
                        <ForgotPasswordForm
                            initialValues={{email: userEmail}}
                            onSubmit={onSubmitEmail}
                            onSignIn={onSignIn}
                        />
                    </Fragment>
                );

            case STEP_CODE:
                return (
                    <Fragment>
                        <div className="page-title_v3">Enter Verification Code</div>
                        <ResetPasswordCodeForm
                            onSubmit={onSubmitCode}
                            onBack={handleBackToEmail}
                            email={userEmail}
                        />
                    </Fragment>
                );

            case STEP_NEW_PASSWORD:
                return (
                    <Fragment>
                        <div className="page-title_v3">
                            {hasOwnPassword ? 'Set New Password' : 'Set Up Your Password'}
                        </div>
                        <ResetPasswordNewPasswordForm onSubmit={onSubmitNewPassword} />
                    </Fragment>
                );

            case STEP_SUCCESS:
                return (
                    <Fragment>
                        <div className="page-title_v3">
                            {hasOwnPassword ? 'Password Changed' : 'Password Set Up'}
                        </div>
                        <div className="fp--form-success-block">
                            <div className="fp--form-success-block-top">
                                <p>
                                    {hasOwnPassword
                                        ? 'Your password has been successfully changed.'
                                        : 'Your password has been successfully set up. You can now sign in with your email and password.'}
                                </p>
                            </div>
                        </div>
                        <form id="reset-password-active-form" onSubmit={e => { e.preventDefault(); e.stopPropagation(); onSignIn(); }}>
                            <div className="form-btn_v3">
                                <button type="submit" className="btn_v3 btn_v3-md btn_v3-primary">
                                    Sign In
                                </button>
                            </div>
                        </form>
                    </Fragment>
                );
        }
    };

    return (
        <div className="auth-form auth-form__auth">
            <div className="auth-form__inner">
                {renderStep()}
            </div>
        </div>
    );
}

export default ResetPasswordPage;
